import { useEffect, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import type { TurnawaySummary } from '@lemontaigu/pms-core';
import { usePms, usePmsReach } from './PmsProvider.js';
import { fmtDate, fmtEuros } from './logic.js';

// « Demandes sans suite » (PB9, PMS-PHONE-BOOKING-BRIEF §2.1): the month's calls
// that ended without a booking, counted anonymously — the demand the hotel did
// not convert. One line per établissement in reach; the dates asked on demand.
// The desk records them when it closes such a call (« Nouvel appel », PB5).

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

export function monthLabelFr(iso: string): string {
  return `${MONTHS_FR[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
}

export function TurnawaysLine({ summary, label }: { summary: TurnawaySummary; label: string | null }) {
  const [open, setOpen] = useState(false);
  if (summary.total === 0) return null;
  return (
    <div className="pms-turnaways">
      <button type="button" className="pms-link" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {label ? `${label} — ` : ''}
        {summary.total} demande{summary.total > 1 ? 's' : ''} sans suite : {summary.denial} sans disponibilité,{' '}
        {summary.regret} offre{summary.regret > 1 ? 's' : ''} déclinée{summary.regret > 1 ? 's' : ''}
        {summary.regret > 0 ? ` (${fmtEuros(summary.regretCents)})` : ''}
      </button>
      {open && (
        <ul className="pms-sub">
          {summary.stays.map((s, i) => (
            <li key={i}>
              {fmtDate(s.arrival)} · {s.nights} nuit{s.nights > 1 ? 's' : ''} · {s.persons} pers. ·{' '}
              {s.kind === 'denial' ? 'sans disponibilité' : 'offre déclinée'}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Turnaways() {
  const pms = usePms();
  const reach = usePmsReach();
  const canRead = useCapability(CAPABILITIES.PMS_RESERVATION_READ);
  const [month] = useState(() => todayIsoParis());
  const [lines, setLines] = useState<{ code: string; label: string | null; summary: TurnawaySummary }[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canRead) return;
    let live = true;
    const sources =
      reach.length > 0
        ? reach.map((b) => ({ code: b.property.code, label: reach.length > 1 ? b.property.labelFr : null, svc: b.services }))
        : [];
    void Promise.all(sources.map((s) => s.svc.turnaways.month(s.code, month))).then((results) => {
      if (!live) return;
      const failed = results.find((r) => !r.ok);
      setError(failed && !failed.ok ? failed.error.message : '');
      setLines(sources.flatMap((s, i) => {
        const r = results[i];
        return r?.ok ? [{ code: s.code, label: s.label, summary: r.value.summary }] : [];
      }));
    });
    return () => {
      live = false;
    };
  }, [canRead, reach, pms, month]);

  if (!canRead || (lines.every((l) => l.summary.total === 0) && !error)) return null;
  return (
    <section className="pms-turnaways-box" aria-label="Demandes sans suite">
      <strong>Demandes sans suite — {monthLabelFr(month)}</strong>
      {error && <p className="auth-err">Erreur : {error}</p>}
      {lines.map((l) => (
        <TurnawaysLine key={l.code} summary={l.summary} label={l.label} />
      ))}
    </section>
  );
}
